<template>
  <article class="property-card">
    <RouterLink :to="`/properties/${property.id}`" class="card-link">
      <div class="card-image-wrap">
        <img
          :src="property.image"
          :alt="property.title"
          class="card-image"
          loading="lazy"
        />
        <span class="badge badge-type" :class="typeBadgeClass">
          {{ typeLabel }}
        </span>
      </div>
      <div class="card-body">
        <p class="card-price">
          {{ formatPrice(property.price, property.type) }}
        </p>
        <h3 class="card-title">{{ property.title }}</h3>
        <p class="card-location muted">
          <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
            <path
              fill="currentColor"
              d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5A2.5 2.5 0 1112 6a2.5 2.5 0 010 5.5z"
            />
          </svg>
          {{ property.location }}
        </p>
        <div class="card-meta">
          <span v-if="property.beds">
            <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
              <path
                fill="currentColor"
                d="M7 14c1.66 0 3-1.34 3-3S8.66 8 7 8s-3 1.34-3 3 1.34 3 3 3zm0-4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm12-3h-8v7H3V7c0-1.1.9-2 2-2h14c1.1 0 2 .9 2 2v10h-2v-2z"
              />
            </svg>
            {{ property.beds }} غرف
          </span>
          <span v-if="property.baths">
            <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
              <path
                fill="currentColor"
                d="M9 2c-1.05 0-2 .95-2 2v1H6c-1.1 0-2 .9-2 2v11c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2h-1V4c0-1.05-.95-2-2-2H9zm0 2h6v1H9V4z"
              />
            </svg>
            {{ property.baths }} حمام
          </span>
          <span>
            <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
              <path
                fill="currentColor"
                d="M3 3h18v2H3V3zm0 4h18v14H3V7zm2 2v10h14V9H5z"
              />
            </svg>
            {{ property.area }} م²
          </span>
        </div>
        <div v-if="property.amenities?.length" class="card-amenities">
          <span v-for="a in property.amenities.slice(0, 3)" :key="a">{{
            a
          }}</span>
        </div>
      </div>
    </RouterLink>
    <div class="card-footer">
      <RouterLink
        :to="`/properties/${property.id}`"
        class="btn btn-primary btn-block"
      >
        عرض التفاصيل
      </RouterLink>
    </div>
  </article>
</template>

<script setup>
import { computed } from "vue";
import { RouterLink } from "vue-router";
import { formatPrice } from "@/data/properties";

const props = defineProps({
  property: { type: Object, required: true },
});

const typeLabel = computed(() =>
  props.property.type === "rent" ? "للإيجار" : "للبيع"
);

const typeBadgeClass = computed(() =>
  props.property.type === "rent" ? "badge-info" : "badge-success"
);
</script>

<style scoped>
.property-card {
  background: var(--surface-card);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-md);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: box-shadow var(--motion), transform var(--motion);
}

.property-card:hover {
  box-shadow: var(--shadow-lg);
  transform: translateY(-2px);
}

.card-link {
  text-decoration: none;
  color: inherit;
  flex: 1;
}

.card-link:hover {
  color: inherit;
}

.card-image-wrap {
  position: relative;
  aspect-ratio: 4 / 3;
  overflow: hidden;
}

.card-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.badge-type {
  position: absolute;
  top: 12px;
  inset-inline-start: 12px;
}

.card-body {
  padding: 20px 24px 16px;
}

.card-price {
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--brand);
  margin: 0 0 8px;
}

.card-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--content-heading);
  margin: 0 0 8px;
  line-height: 1.4;
}

.card-location {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.875rem;
  margin: 0 0 12px;
}

.card-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 0.8125rem;
  color: var(--content-muted);
}

.card-meta span {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.card-footer {
  padding: 0 24px 24px;
}

.card-amenities {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 12px;
}

.card-amenities span {
  font-size: 0.75rem;
  padding: 3px 8px;
  background: var(--surface-page);
  border-radius: var(--radius-md);
  color: var(--content-muted);
}

.btn-block {
  width: 100%;
}
</style>
